import { useState } from "react";
import { Star, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export function Stars({ value, size = 14, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} weight={i <= Math.round(value) ? "fill" : "regular"} className={i <= Math.round(value) ? "text-amber-400" : "text-zinc-600"} />)}
    </span>
  );
}

export function RatingBadge({ rating, size = 12, testId }) {
  if (!rating || !rating.count) return null;
  return (
    <span className="inline-flex items-center gap-1 tracker text-[10px] text-amber-400" data-testid={testId}>
      <Star size={size} weight="fill" /> {rating.avg.toFixed(1)} <span className="text-zinc-500">({rating.count})</span>
    </span>
  );
}

export function ReviewModal({ booking, initialRating, onClose, onSaved }) {
  const [rating, setRating] = useState(booking.review?.rating || initialRating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(booking.review?.comment || "");
  const [saving, setSaving] = useState(false);
  const labels = ["", "Not for me", "Okay", "Good", "Great", "Loved it"];

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Pick a star rating");
    setSaving(true);
    try {
      await api.put(`/member/bookings/${booking.id}/review`, { rating, comment: comment.trim() });
      toast.success(booking.review ? "Review updated" : "Thanks for your review!");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-flat w-full max-w-md p-8 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="tracker text-zinc-500 text-[10px] mb-2">[ How was your class? ]</p>
            <h2 className="font-display text-3xl leading-none">{booking.title}</h2>
            <p className="text-zinc-500 text-xs mt-1">{booking.business?.brand_name}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="review-close"><X size={20} weight="bold" /></button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" onClick={() => setRating(i)} onMouseEnter={() => setHover(i)} className="transition-transform hover:scale-110" data-testid={`review-star-${i}`} aria-pressed={rating === i}>
                <Star size={32} weight={i <= (hover || rating) ? "fill" : "regular"} className={i <= (hover || rating) ? "text-amber-400" : "text-zinc-600"} />
              </button>
            ))}
          </div>
          <span className="tracker text-[10px] text-zinc-400 min-w-[70px]" data-testid="review-label">{labels[hover || rating]}</span>
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 600))} rows={3} placeholder="Anything you'd like to say? (optional)" className="w-full px-4 py-3 border text-sm" data-testid="review-comment" />
        <p className="text-[11px] text-zinc-500">Shown publicly as "{(booking.reviewer_display || "First name L.")}". You can edit this for 7 days.</p>
        <button type="submit" disabled={saving} className="btn-primary w-full justify-center" data-testid="review-submit">{saving ? "Saving…" : booking.review ? "Update review" : "Post review"}</button>
      </form>
    </div>
  );
}
